import { useCallback, useEffect, useState } from "react"
import { ActionLogQueryState, ActionLogResponse } from "./action-log.type"
import { getActionLogMiddleware } from "./action-log.api"
import { useBoolean, useNumber, useString, useWindowSize } from "helpers/hooks"
import { cloneDeep, debounce } from "lodash"
import { ReactComponent as IconSearchNormal } from "assets/images/icons/icon-search-normal.svg"
import InputDefault from "components/Input/InputDefault"
import { ReactComponent as IconClearSearch } from "assets/images/icons/icon-clear-search.svg"
import { useAppSelector } from "hooks/useApp"
import { PATH } from "constants/path"
import { pushTo } from "helpers/history"
import { hasPermission } from "helpers/permissions"
import { PERMISSIONS } from "constants/permissions"
import { formatDateForConversationMessage } from "helpers/utils"
import { Avatar } from "components/Avatar"
import Button from "components/Button/Button"
import Tippy from "@tippyjs/react"
import DotCard from "pages/conversations/atoms/DotCard"
import NoData from "pages/projects/molecules/NoData"
import { ActionLogFilterCard } from "./organisms/ActionLogFilterCard"
import { excelService } from "helpers/excel"
import { saveAs } from "file-saver"
import { formatDate } from "helpers/utils"
import { getUserAddGroupsOrganizationMiddleware } from "pages/groups/groups.api"
import ButtonHasIcon from "components/Button/ButtonHasIcon"
import { ReactComponent as IconDownload } from "assets/images/icons/icon-download-history.svg"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { isDecryptionBlocked } from "helpers/decryptionBlocked"
import LockIcon from "assets/images/icons/icon-lock.svg"

interface IconConfig {
  bg: string
  color: string
  content: string
  icon: JSX.Element
}

const ICONS: Record<string, Omit<IconConfig, "icon">> = {
  project_build_components: {
    bg: "#8F00FF26",
    color: "#8F00FF",
    content: "Project Build Component",
  },
  project_components: {
    bg: "#8F00FF26",
    color: "#8F00FF",
    content: "Project Component",
  },
  project_builds: {
    bg: "#FDBE4426",
    color: "#F7AC1B",
    content: "Project Build",
  },
  sync_commit_components: {
    bg: "#0A5AF526",
    color: "#0A5AF5",
    content: "Sync Commit Component",
  },
  users: {
    bg: "#F33EB626",
    color: "#F33EB6",
    content: "User",
  },
  default: {
    bg: "#56A01226",
    color: "#56A012",
    content: "Project",
  },
}

export const ActionLogPage = () => {
  const { isMobile } = useWindowSize()
  const userInfo = useAppSelector((state) => state.userInfo)
  const [actionLogs, setActionLogs] = useState<ActionLogResponse[]>([])

  const [queryState, setQueryState] = useState<ActionLogQueryState>({
    page: 1,
    pageSize: 25,
    search: "",
    models: "",
  })
  const [projects, setProjects] = useState<
    {
      id: string
      name: string
      code: string
    }[]
  >([])
  const [users, setUsers] = useState<
    {
      id: string
      fullname: string
      email: string
    }[]
  >([])
  const totalPage = useNumber(0)
  const search = useString()
  const isLoadingMore = useBoolean(false)
  const isMoreData = useBoolean(false)
  const showFilter = useBoolean(false)
  const loadingPage = useBoolean(true)

  useEffect(() => {
    if (!userInfo?.id) {
      return
    }
    if (!userInfo.last_organization_id) {
      pushTo(PATH.notFound)
      return
    }
    if (!hasPermission(userInfo, PERMISSIONS.ACTIVITY_LOG_VIEW)) {
      pushTo(PATH.notFound)
      return
    }
  }, [userInfo?.id])

  const isInitialMount = useBoolean(true)

  // Load initial data with loading page
  useEffect(() => {
    if (!userInfo?.id) {
      return
    }

    if (!hasPermission(userInfo, PERMISSIONS.ACTIVITY_LOG_VIEW)) {
      return
    }
    if (isInitialMount.value) {
      isInitialMount.setValue(false)
      getActionLog(queryState, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id])

  // Handle search and filter changes without loading page
  useEffect(() => {
    if (!userInfo?.id) {
      return
    }
    if (!hasPermission(userInfo, PERMISSIONS.ACTIVITY_LOG_VIEW)) {
      return
    }
    if (!isInitialMount.value) {
      getActionLog(queryState, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryState.search,
    queryState.models,
    queryState.startDate,
    queryState.endDate,
    queryState.projectIds,
    queryState.userIds,
  ])

  const getActionLog = async (
    newQueryState: ActionLogQueryState,
    isInitialLoad = false
  ) => {
    try {
      if (isInitialLoad) {
        loadingPage.setValue(true)
      }
      const response = await getActionLogMiddleware(newQueryState)
      isMoreData.setValue(
        response.logs.length < newQueryState.pageSize ? false : true
      )
      setActionLogs(response.logs)
      if (isInitialLoad) {
        setProjects(response.projects)
        const users = await getUserAddGroupsOrganizationMiddleware("all-admin")
        setUsers(users)
      }
    } catch (error) {
      console.error("Error fetching action logs:", error)
      setActionLogs([])
      totalPage.setValue(0)
    } finally {
      if (isInitialLoad) {
        loadingPage.setValue(false)
      }
    }
  }

  const onPreventBubble = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce((newSearch: string, newQueryState: ActionLogQueryState) => {
      setQueryState({
        ...newQueryState,
        search: newSearch.trim(),
        page: 1,
      })
    }, 500),
    []
  )

  const handleLoadMore = async () => {
    if (isLoadingMore.value) {
      return
    }
    isLoadingMore.setValue(true)
    try {
      const newQueryState = cloneDeep(queryState)
      newQueryState.page = newQueryState.page + 1

      const response = await getActionLogMiddleware(newQueryState)

      isMoreData.setValue(
        response.logs.length < newQueryState.pageSize ? false : true
      )
      setQueryState(newQueryState)
      const newData = cloneDeep(actionLogs).concat(response.logs)
      setActionLogs(newData)
      isLoadingMore.setValue(false)
    } catch (error) {
      isLoadingMore.setValue(false)
    }
  }

  const renderIcon = (model: string) => {
    const { bg, color, content } = ICONS[model] || ICONS.default

    const ICON_SVGS: Record<string, JSX.Element> = {
      project_build_components: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            opacity="0.4"
            d="M21.0405 7.19L12.0004 12.42L2.96045 7.19C3.36045 6.45 3.94045 5.8 4.59045 5.44L9.93045 2.48C11.0704 1.84 12.9304 1.84 14.0704 2.48L19.4104 5.44C20.0604 5.8 20.6405 6.45 21.0405 7.19Z"
            fill={color}
          />
          <path
            opacity="0.6"
            d="M12.0006 12.4199V21.9999C11.2506 21.9999 10.5006 21.8399 9.93062 21.5199L4.59063 18.5599C3.38063 17.8899 2.39062 16.2099 2.39062 14.8299V9.16994C2.39062 8.52994 2.61062 7.82994 2.96062 7.18994L12.0006 12.4199Z"
            fill={color}
          />
          <path
            d="M21.6105 9.16994V14.8299C21.6105 16.2099 20.6205 17.8899 19.4105 18.5599L14.0705 21.5199C13.5005 21.8399 12.7505 21.9999 12.0005 21.9999V12.4199L21.0405 7.18994C21.3905 7.82994 21.6105 8.52994 21.6105 9.16994Z"
            fill={color}
          />
        </svg>
      ),
      project_components: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            opacity="0.4"
            d="M21.0405 7.19L12.0004 12.42L2.96045 7.19C3.36045 6.45 3.94045 5.8 4.59045 5.44L9.93045 2.48C11.0704 1.84 12.9304 1.84 14.0704 2.48L19.4104 5.44C20.0604 5.8 20.6405 6.45 21.0405 7.19Z"
            fill={color}
          />
          <path
            opacity="0.6"
            d="M12.0006 12.4199V21.9999C11.2506 21.9999 10.5006 21.8399 9.93062 21.5199L4.59063 18.5599C3.38063 17.8899 2.39062 16.2099 2.39062 14.8299V9.16994C2.39062 8.52994 2.61062 7.82994 2.96062 7.18994L12.0006 12.4199Z"
            fill={color}
          />
          <path
            d="M21.6105 9.16994V14.8299C21.6105 16.2099 20.6205 17.8899 19.4105 18.5599L14.0705 21.5199C13.5005 21.8399 12.7505 21.9999 12.0005 21.9999V12.4199L21.0405 7.18994C21.3905 7.82994 21.6105 8.52994 21.6105 9.16994Z"
            fill={color}
          />
        </svg>
      ),
      project_builds: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            opacity="0.4"
            d="M15.4067 19.146C15.4159 19.2712 15.431 19.3952 15.4526 19.521H8.54639C8.56885 19.3967 8.58396 19.2722 8.59326 19.146H15.4067ZM19.521 15.4536C19.3967 15.4311 19.2723 15.4151 19.146 15.4058V8.59326C19.2713 8.5841 19.3952 8.56807 19.521 8.54639V15.4536ZM4.479 8.54541C4.60331 8.56787 4.72772 8.58397 4.854 8.59326V15.4058C4.72878 15.4149 4.60475 15.43 4.479 15.4517V8.54541ZM15.4536 4.479C15.4312 4.60327 15.416 4.72777 15.4067 4.854H8.59326C8.58412 4.72876 8.56902 4.60477 8.54736 4.479H15.4536Z"
            fill={color}
            stroke={color}
          />{" "}
          <path
            d="M5.12549 16.396C5.21378 16.396 5.25548 16.3963 5.29248 16.4009L5.30322 16.4028L5.31494 16.4038C5.50308 16.4189 5.68724 16.457 5.86865 16.5122L5.88037 16.5151L5.89307 16.519C5.94141 16.5311 5.98774 16.5468 6.02002 16.563L6.03271 16.5698L6.04639 16.5757C6.11103 16.6034 6.16274 16.6256 6.22119 16.6548C6.2792 16.6838 6.30731 16.6985 6.33252 16.7153L6.34912 16.7261L6.36572 16.7358C6.74359 16.9474 7.05353 17.2564 7.26514 17.6343L7.27393 17.6519L7.28467 17.6675C7.30151 17.6927 7.31615 17.7207 7.34521 17.7788C7.37446 17.8373 7.39662 17.889 7.42432 17.9536L7.43018 17.9673L7.43701 17.98C7.45314 18.0122 7.46985 18.0587 7.48193 18.1069L7.48486 18.1196L7.48877 18.1313C7.54403 18.3129 7.5821 18.4977 7.59717 18.686V18.6968L7.59912 18.7085C7.6037 18.7454 7.604 18.7874 7.604 18.8755C7.60398 19.0458 7.58884 19.2073 7.55811 19.3687C7.52961 19.518 7.47794 19.6761 7.41455 19.8267V19.8276C7.03254 20.7367 6.12973 21.3539 5.12549 21.354C3.76091 21.354 2.64617 20.24 2.646 18.8755C2.646 17.8628 3.26331 16.9688 4.17334 16.5864L4.18311 16.5815L4.19385 16.5767C4.32123 16.5164 4.46117 16.4732 4.61865 16.4429C4.7927 16.4112 4.95568 16.396 5.12549 16.396ZM18.8755 16.396C19.0458 16.396 19.2073 16.4112 19.3687 16.4419C19.5181 16.4704 19.676 16.523 19.8267 16.5864H19.8276C20.7366 16.9685 21.354 17.8712 21.354 18.8755C21.3538 20.2399 20.2399 21.3538 18.8755 21.354C17.8629 21.354 16.9689 20.7375 16.5864 19.8276L16.5815 19.8169L16.5767 19.8071L16.5347 19.7075C16.5216 19.6739 16.5096 19.6394 16.4985 19.604L16.4419 19.3774C16.4108 19.2051 16.396 19.0438 16.396 18.8755C16.396 18.7874 16.3963 18.7454 16.4009 18.7085L16.4048 18.6772V18.6714L16.4067 18.6665L16.4126 18.6089C16.4181 18.5479 16.4286 18.4892 16.4448 18.4009C16.4472 18.3881 16.4492 18.3746 16.4517 18.3608C16.4883 18.1634 16.554 17.972 16.6509 17.7856L16.6548 17.7788C16.6839 17.7206 16.6985 17.6927 16.7153 17.6675L16.7261 17.6519L16.7358 17.6343C16.9474 17.2566 17.2566 16.9474 17.6343 16.7358L17.6519 16.7261L17.6675 16.7153C17.6927 16.6985 17.7206 16.6839 17.7788 16.6548L17.7856 16.6509C17.972 16.554 18.1634 16.4883 18.3608 16.4517L18.3599 16.4507C18.3738 16.4481 18.3879 16.4472 18.4009 16.4448C18.4892 16.4286 18.5479 16.4181 18.6089 16.4126L18.6665 16.4067L18.6724 16.4048H18.6772L18.7085 16.4009C18.7454 16.3963 18.7874 16.396 18.8755 16.396ZM5.12549 2.646C6.13807 2.64613 7.03223 3.26341 7.41455 4.17334L7.41846 4.18311L7.42334 4.19385C7.48352 4.32099 7.52684 4.46055 7.55713 4.61768V4.61865C7.58877 4.7927 7.604 4.95568 7.604 5.12549C7.604 5.21378 7.60375 5.25548 7.59912 5.29248L7.59717 5.3042L7.59619 5.31592C7.5811 5.51213 7.54376 5.69197 7.48291 5.87451L7.4751 5.89893L7.46924 5.92432C7.46224 5.95566 7.44279 6.0097 7.40869 6.08643L7.40479 6.09521L7.40186 6.104C7.39288 6.12637 7.37556 6.16049 7.34521 6.22119C7.31616 6.27931 7.3015 6.30727 7.28467 6.33252L7.27393 6.34912L7.26514 6.36572C7.05353 6.7436 6.7436 7.05353 6.36572 7.26514L6.34912 7.27393L6.33252 7.28467C6.30727 7.3015 6.27931 7.31616 6.22119 7.34521C6.16049 7.37556 6.12637 7.39288 6.104 7.40186L6.09521 7.40479L6.08643 7.40869C6.0097 7.44279 5.95566 7.46224 5.92432 7.46924L5.89893 7.4751L5.87451 7.48291C5.69197 7.54376 5.51213 7.5811 5.31592 7.59619L5.3042 7.59717L5.29248 7.59912C5.25548 7.60375 5.21378 7.604 5.12549 7.604C4.95512 7.604 4.79372 7.58883 4.63232 7.55811C4.55743 7.54384 4.48004 7.524 4.40283 7.49951L4.17334 7.41455C3.26424 7.03257 2.64613 6.12979 2.646 5.12549C2.646 3.7608 3.7608 2.646 5.12549 2.646ZM18.8755 2.646C20.24 2.64617 21.354 3.76091 21.354 5.12549C21.3539 6.13802 20.7375 7.03219 19.8276 7.41455L19.8169 7.41846L19.8071 7.42334C19.6795 7.48378 19.5392 7.5268 19.3813 7.55713C19.2075 7.58873 19.0451 7.60398 18.8755 7.604C18.7874 7.604 18.7454 7.6037 18.7085 7.59912L18.6958 7.59717L18.6831 7.59619L18.5317 7.58057C18.3842 7.56049 18.2465 7.52721 18.1099 7.47803L18.0854 7.46924L18.061 7.46338C18.0129 7.45132 17.9673 7.43453 17.9351 7.41846L17.9028 7.40283L17.8687 7.39209L17.8657 7.39014C17.8578 7.38639 17.8482 7.38133 17.8276 7.37061C17.815 7.36401 17.7974 7.35451 17.7788 7.34521C17.7207 7.31615 17.6927 7.30151 17.6675 7.28467L17.6519 7.27393L17.6343 7.26514L17.4956 7.18115C17.1804 6.97501 16.921 6.6963 16.7358 6.36572L16.7261 6.34912L16.7153 6.33252C16.6985 6.30731 16.6838 6.2792 16.6548 6.22119C16.6456 6.20291 16.6369 6.18586 16.6304 6.17334C16.6197 6.15296 16.6146 6.14316 16.6108 6.13525C16.6101 6.13379 16.6093 6.13222 16.6089 6.13135L16.5972 6.09717L16.5815 6.06592L16.5366 5.93896L16.5308 5.91455L16.522 5.89111L16.478 5.75342C16.4391 5.61513 16.4153 5.47285 16.4038 5.31787L16.4028 5.30518L16.4009 5.29248L16.396 5.12549C16.396 4.95514 16.4112 4.7937 16.4419 4.63232C16.4562 4.55746 16.476 4.48001 16.5005 4.40283L16.5864 4.17334C16.9684 3.26414 17.8711 2.646 18.8755 2.646Z"
            fill={color}
            stroke={color}
          />{" "}
        </svg>
      ),
      sync_commit_components: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.4"
            d="M5.75 16.11V7.90001C7.04 7.57001 8 6.40001 8 5.01001C8 3.35001 6.66 2.01001 5 2.01001C3.34 2.01001 2 3.35001 2 5.01001C2 6.41001 2.96 7.57001 4.25 7.90001V16.11C2.96 16.44 2 17.61 2 19C2 20.66 3.34 22 5 22C6.66 22 8 20.66 8 19C8 17.6 7.04 16.44 5.75 16.11Z"
            fill={color}
          />
          <path
            d="M19.75 16.1099V6.49993C19.75 4.97993 18.52 3.74993 17 3.74993H14.07L15.48 2.57993C15.8 2.30993 15.84 1.83993 15.58 1.51993C15.31 1.19993 14.84 1.15993 14.52 1.41993L11.52 3.91993C11.35 4.05993 11.25 4.26993 11.25 4.49993C11.25 4.72993 11.35 4.92993 11.52 5.07993L14.52 7.57993C14.66 7.69993 14.83 7.74993 15 7.74993C15.21 7.74993 15.43 7.65993 15.58 7.47993C15.85 7.15993 15.8 6.68993 15.48 6.41993L14.07 5.24993H17C17.69 5.24993 18.25 5.80993 18.25 6.49993V16.1099C16.96 16.4399 16 17.6099 16 18.9999C16 20.6599 17.34 21.9999 19 21.9999C20.66 21.9999 22 20.6599 22 18.9999C22 17.5999 21.04 16.4399 19.75 16.1099Z"
            fill={color}
          />
        </svg>
      ),
      users: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.4"
            d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
            fill={color}
          />
          <path
            d="M12.0002 14.5C6.99015 14.5 2.91016 17.86 2.91016 22C2.91016 22.28 3.13016 22.5 3.41016 22.5H20.5901C20.8701 22.5 21.0901 22.28 21.0901 22C21.0901 17.86 17.0102 14.5 12.0002 14.5Z"
            fill={color}
          />
        </svg>
      ),
      default: (
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.02 6.07002V6.57002H16.52H16.76C18.7612 6.57002 20.0078 7.02885 20.719 7.81529C21.5053 8.68516 21.5498 9.86635 21.4726 10.8566C21.4674 10.8967 21.4644 10.9313 21.4623 10.9549L21.4619 10.9605C21.4593 10.9895 21.4582 10.9993 21.4568 11.0078L21.4543 11.0224L21.4528 11.0371L21.403 11.5048L21.4028 11.5071L21.3857 11.6605C21.022 11.9222 20.6496 12.1611 20.2539 12.3847L20.2539 12.3846L20.2427 12.3913C20.2241 12.4024 20.2076 12.4129 20.1944 12.4214L20.1617 12.4423C20.1452 12.4527 20.1357 12.4581 20.1264 12.4628L20.1118 12.4701L20.0977 12.4783C18.3706 13.4874 16.459 14.1763 14.4801 14.4964L14.0897 14.5596L14.0613 14.954C14.0299 15.3883 13.9226 15.7614 13.6547 16.0319C13.3917 16.2975 12.8872 16.55 11.87 16.55C10.8545 16.55 10.3546 16.2959 10.0933 16.0312C9.82542 15.7599 9.71851 15.3886 9.6777 14.9624L9.64093 14.5783L9.26008 14.5165C7.40378 14.2152 5.57851 13.6031 3.92084 12.6919L3.92086 12.6918L3.91527 12.6888C3.76198 12.6071 3.63029 12.5365 3.51036 12.4594L3.49684 12.4507L3.4828 12.4429C3.19267 12.2818 2.91361 12.1054 2.6358 11.9224L2.55772 11.1026L2.55661 11.0909L2.55495 11.0793L2.54649 11.0201C2.4484 10.0204 2.4506 8.74828 3.28122 7.82495C3.99266 7.03851 5.22948 6.58002 7.23998 6.58002H7.47998H7.97998V6.08002V6.03002C7.97998 5.1604 7.99421 4.3797 8.37059 3.79411C8.71061 3.26508 9.44536 2.77002 11.24 2.77002H12.76C14.5555 2.77002 15.2899 3.26312 15.6296 3.79069C16.0056 4.3748 16.02 5.15499 16.02 6.03002V6.07002ZM10.63 12.58H10.13V13.08V14.34L10.13 14.3635C10.1299 14.5988 10.1299 14.8274 10.1527 15.0186C10.1766 15.2188 10.2328 15.458 10.4081 15.6575C10.5873 15.8615 10.8255 15.9507 11.0531 15.9951C11.2766 16.0388 11.5484 16.05 11.86 16.05C12.1691 16.05 12.4394 16.0407 12.6608 16.0003C12.8855 15.9593 13.1271 15.8749 13.3095 15.6702C13.488 15.47 13.5438 15.2275 13.5674 15.026C13.59 14.8325 13.59 14.5987 13.59 14.354L13.59 14.33V13.08V12.58H13.09H10.63ZM15.09 6.58002H15.59V6.08002V6.03002V6.00666C15.59 5.60304 15.59 5.22042 15.5499 4.89229C15.5085 4.55365 15.4184 4.21107 15.1921 3.92265C14.9599 3.62663 14.6324 3.44789 14.2345 3.34295C13.8451 3.24027 13.358 3.20002 12.76 3.20002H11.24C10.6423 3.20002 10.1555 3.2396 9.76653 3.34126C9.36906 3.44513 9.0411 3.62257 8.80834 3.91826C8.58154 4.20638 8.49147 4.54914 8.45007 4.88816C8.40995 5.21672 8.40996 5.60054 8.40998 6.00653L8.40998 6.03002V6.07002V6.08002V6.58002H8.90998H15.09Z"
            fill={color}
            stroke={color}
          />{" "}
          <path
            opacity="0.4"
            d="M20.6022 13.3217L20.6022 13.3218L20.6072 13.3188L20.6822 13.2738L20.7482 13.2342C20.9299 13.1351 21.108 13.0306 21.2829 12.9218L20.7524 18.6308C20.6513 19.5908 20.4117 20.4582 19.757 21.0972C19.1051 21.7335 17.9502 22.23 15.82 22.23H8.19997C6.06974 22.23 4.91465 21.7335 4.26266 21.0982C3.60801 20.4603 3.36856 19.5952 3.26752 18.6404L2.75376 13.1575C2.82932 13.2022 2.90588 13.2462 2.98348 13.2895C3.13652 13.3869 3.29627 13.4721 3.43687 13.547L3.44094 13.5492C5.09286 14.4664 6.89632 15.0929 8.73336 15.4219C8.8143 15.8556 8.9899 16.327 9.37904 16.7187C9.89442 17.2374 10.6969 17.53 11.87 17.53C13.0442 17.53 13.8474 17.2343 14.3626 16.7107C14.7528 16.3143 14.9277 15.8369 15.0079 15.3984C16.9756 15.0285 18.8748 14.331 20.6022 13.3217Z"
            fill={color}
            stroke={color}
          />
        </svg>
      ),
    }

    const icon = ICON_SVGS[model] || ICON_SVGS.default

    return (
      <Tippy
        className="custom-tippy-menu-left flex-1"
        placement="right"
        content={
          <span className="font-normal text-[13px] leading-[22px]">
            {content}
          </span>
        }
      >
        <div
          className="absolute top-0 left-0 w-[32px] h-[32px] rounded-full flex items-center justify-center"
          style={{ background: bg }}
        >
          {icon}
        </div>
      </Tippy>
    )
  }

  const renderLoadingPage = () => {
    return (
      <div className="w-full flex flex-col gap-[16px] rounded-[8px] border border-[#E4E4E4] bg-[#FFFFFF] p-[16px]">
        <div className="flex items-center w-full">
          <div className="h-[12px] ms-2 bg-[#EFEFEF] rounded-full dark:bg-gray-600 w-full"></div>
        </div>
        <div className="flex items-center w-full">
          <div className="h-[12px] ms-2 bg-[#EFEFEF] rounded-full dark:bg-gray-600 w-full"></div>
        </div>
        <div className="flex items-center w-full">
          <div className="h-[12px] ms-2 bg-[#EFEFEF] rounded-full dark:bg-gray-600 w-full"></div>
        </div>
        <div className="flex items-center w-full">
          <div className="h-[12px] ms-2 bg-[#EFEFEF] rounded-full dark:bg-gray-600 w-full"></div>
        </div>
        <div className="flex items-center w-full">
          <div className="h-[12px] ms-2 bg-[#EFEFEF] rounded-full dark:bg-gray-600 w-full"></div>
        </div>
      </div>
    )
  }

  const handleExportExcel = async () => {
    try {
      const response = await getActionLogMiddleware({
        ...queryState,
        is_export: true,
      })
      const exportData = response.logs.map((log, index) => ({
        Index: index + 1,
        Model: log.model,
        Date: formatDate(log.created_at, "DD-MM-YYYY HH:mm:ss"),
        Content: log.content,
        FullName: log.user.fullname || log.user.email,
        Email: log.user.email,
        ProjectId: log.project_id || "",
        ProjectName: log.project_name || "",
        ProjectCode: log.project_code || "",
        Relation: log.relation_id || "",
      }))

      const data = await excelService.toExcel(exportData)
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      })
      const fileName = `tracelium_action_logs_${new Date().getTime()}.xlsx`
      saveAs(blob, fileName)
    } catch (error) {
      toast(
        <LabelNotificationPage
          messenger="Export action logs failed!"
          type="error"
        />
      )
    }
  }
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between bg-[#FFFFFF] border-b border-[#E4E4E4] min-h-[63px] px-[24px]">
        <h2 className="text-[16px] font-semibold lh-26 text-black">
          Action logs
        </h2>
        <div className="flex items-center gap-[12px]">
          {!isMobile ? (
            <div
              className=" bg-grayWhite2 p-2 rounded-full flex items-center w-[400px]"
              onClick={onPreventBubble}
              style={{
                width: isMobile ? 150 : 400,
              }}
            >
              <IconSearchNormal
                style={{
                  width: 20,
                }}
                values={search.value}
                onClick={onPreventBubble}
                onChange={(e) => {
                  onPreventBubble(e)
                }}
              />
              <InputDefault
                value={search.value}
                placeholder="Search by project, build, component code, or user"
                style={{
                  border: "none",
                  boxShadow: "none",
                  height: 30,
                  fontWeight: 400,
                }}
                classCustom="h-11 border rounded bg-grayWhite2 pl-3 pr-3 text-sm font-light text-[#111111] focus:outline-none"
                onClick={onPreventBubble}
                onChange={(e) => {
                  onPreventBubble(e)
                  search.setValue(e.target.value)
                  debounceSearch(e.target.value, cloneDeep(queryState))
                }}
              />
              {search.value ? (
                <div
                  onClick={(e) => {
                    onPreventBubble(e)
                    search.setValue("")
                    setQueryState({
                      ...queryState,
                      search: "",
                    })
                  }}
                  className="h-5 w-5 flex items-center justify-center custom-icon-clear-search cursor-pointer"
                >
                  <IconClearSearch />
                </div>
              ) : null}
            </div>
          ) : null}

          <ActionLogFilterCard
            showFilter={showFilter}
            queryState={queryState}
            setQueryState={setQueryState}
            projects={projects}
            users={users}
          />
          <ButtonHasIcon
            title="Export"
            newIcon={
              <IconDownload
                className="icon-download-blue"
                style={{
                  width: 20,
                  height: 20,
                }}
              />
            }
            widthButton={isMobile ? 80 : 100}
            onClick={handleExportExcel}
          />
        </div>
      </div>
      <div className="flex flex-col py-6 pl-6 flex-1 overflow-hidden">
        <div
          className="flex flex-col  pr-6 overflow-auto gap-6"
          style={{
            height: actionLogs.length ? undefined : "100%",
          }}
        >
          {loadingPage.value ? (
            renderLoadingPage()
          ) : actionLogs.length ? (
            <div className="w-full flex flex-col  rounded-[8px] border border-[#E4E4E4] bg-[#FFFFFF] p-[16px]">
              {actionLogs.map((activity, index) => {
                return (
                  <div key={index} className="flex items-center gap-x-[16px]">
                    <div className="min-w-[32px] w-[32px] flex justify-center h-full relative">
                      <div
                        className="h-full w-[2px]"
                        style={{
                          borderLeft: "2px dashed #E4E4E4",
                        }}
                      ></div>
                      {renderIcon(activity.model)}
                    </div>
                    <div className="flex items-start mb-4">
                      <div
                        style={{
                          width: 24,
                          marginTop: 4,
                        }}
                      >
                        <Avatar
                          width="24px"
                          height="24px"
                          name={activity.user.fullname}
                          avatar={activity.user.avatar}
                          textcolor="white"
                          contentsize="11px"
                          justOneLetter={true}
                        />
                      </div>
                      <div className="flex flex-col ml-[8px]">
                        {isDecryptionBlocked(activity.content) ? (
                          <div className="flex items-center gap-[6px]" style={{ color: "#9CA3AF" }}>
                            <img src={LockIcon} alt="lock" style={{ width: 14, height: 14, opacity: 0.5 }} />
                            <span style={{ fontSize: 13 }}>Content unavailable - encryption error</span>
                          </div>
                        ) : (
                          <p
                            className="font-normal"
                            style={{
                              color: "#111111",
                              fontSize: 13,
                              lineHeight: "22px",
                              wordBreak: "break-word",
                            }}
                            dangerouslySetInnerHTML={{ __html: activity.content }}
                          ></p>
                        )}
                        <div className="flex items-center mt-[4px]">
                          {activity.project_code ? (
                            <>
                              <p
                                className="font-normal underline text-[13px] text-[#0A5AF5] cursor-pointer"
                                onClick={() => {
                                  pushTo(PATH.projectComponent, {
                                    idProject: activity.project_id,
                                  })
                                }}
                              >
                                {`${activity.project_code}`}
                              </p>
                              <DotCard />
                            </>
                          ) : null}
                          <p
                            className="font-normal"
                            style={{
                              fontSize: 13,
                              lineHeight: "22px",
                              color: "#7A7A7A",
                            }}
                          >
                            {formatDateForConversationMessage(
                              activity.created_at
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
              {isMoreData.value ? (
                <div className="flex justify-center mt-4">
                  <Button
                    title="Load more"
                    sizeBtn="small"
                    colorBtn="white"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore.value}
                    isDisabledBtn
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="h-full">
              <NoData context="No data" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
